if (!window.__bundleBuilderInit) {
  window.__bundleBuilderInit = true;

  // Consolidated cart model:
  //  - Only two real cart lines ever exist for a build: one "AM Pack" line
  //    and (if used) one "PM Pack" line, each at whichever capsule-count
  //    variant matches the current total, so Shopify charges the exact
  //    tiered price automatically. No individual ingredient ever becomes
  //    its own cart/checkout line.
  //  - The chosen ingredients are recorded on that same fee line as two
  //    properties: "Contains" (human-readable, e.g. "Biotin x2,
  //    Multivitamin x1") and hidden "_IngredientData" (machine-parseable,
  //    "variantId:qty|variantId:qty...", used to restore steppers).
  //    "Contains" has no underscore, so Shopify's native rendering shows
  //    it to the customer in cart/checkout/order confirmation — the
  //    underscore-prefixed one stays hidden from customers but is still
  //    visible to staff on the Admin order detail page for packing.
  //  - A fee line only exists once a pack is actually valid (AM has
  //    reached its 4-capsule minimum). Below that, there's nothing in the
  //    cart to represent the in-progress picks — so they're saved to
  //    localStorage as a draft instead, and only overwritten by cart data
  //    for a pack that currently HAS a priced line. This is what lets an
  //    in-progress, below-minimum build survive a reload or an external
  //    cart change (e.g. removing the other pack's line) without getting
  //    wiped back to 0.

  const PACK_LABELS = { 1: 'AM', 2: 'PM' };
  const DRAFT_KEY = 'bundle_builder_draft_v1';
  // Appstle's "My Subscription Plan" (Monthly Subscription, 0% discount),
  // attached to both AM Pack and PM Pack in Appstle admin. Every Build
  // Your Own pack is subscription-only — no one-time option — so this is
  // always included on the fee line.
  const BUNDLE_SELLING_PLAN_ID = 692767785255;

  function getState(container) {
    if (!container._bundleState) {
      container._bundleState = {
        amFeeLineKey: null,
        pmFeeLineKey: null,
        amFeeVariantId: null,
        pmFeeVariantId: null,
        amFeeIngredientData: null,
        pmFeeIngredientData: null,
        // Old-style per-ingredient $0 line keys found still sitting in the
        // cart from before ingredients were consolidated onto the fee
        // line — queued for removal on the next syncCart().
        legacyLineKeys: [],
        amConfig: null,
        pmConfig: null,
        syncing: false,
        pendingSync: false,
      };
    }
    return container._bundleState;
  }

  function parseConfig(container, pack) {
    const el = container.querySelector(`script[data-bundle-pack-config="${pack === 1 ? 'am' : 'pm'}"]`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  async function fetchWithRetry(url, options, retries = 1) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      return response;
    } catch (error) {
      if (retries <= 0) throw error;
      await new Promise((resolve) => setTimeout(resolve, 400));
      return fetchWithRetry(url, options, retries - 1);
    }
  }

  async function fetchCart() {
    const response = await fetch(`${FoxTheme.routes.cart_url}`, FoxTheme.utils.fetchConfig('json', 'GET'));
    return response.json();
  }

  // Tells <cart-drawer> to refetch its own markup and refresh the header
  // count badge — it does its own single fetch internally, so we don't
  // duplicate that work here.
  function notifyCartChanged() {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { open: false },
      })
    );
  }

  function stepperEls(card, pack) {
    return {
      qtyEl: card.querySelector(`[data-pack-qty="${pack}"]`),
      stepperEl: card.querySelector(`[data-pack-stepper="${pack}"]`),
    };
  }

  function getQty(card, pack) {
    const { qtyEl } = stepperEls(card, pack);
    return qtyEl ? parseInt(qtyEl.textContent.trim(), 10) || 0 : 0;
  }

  function setQtyDisplay(card, pack, qty) {
    const { qtyEl } = stepperEls(card, pack);
    if (qtyEl) qtyEl.textContent = String(qty);
  }

  function sumPack(container, pack) {
    let sum = 0;
    container.querySelectorAll(`[data-pack-qty="${pack}"]`).forEach((el) => {
      sum += parseInt(el.textContent.trim(), 10) || 0;
    });
    return sum;
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ---- Local draft (survives reloads / external cart changes even when a
  // pack has no cart line yet to hold its state) ----

  function readDraft() {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return { am: {}, pm: {} };
      const parsed = JSON.parse(raw);
      return { am: parsed.am || {}, pm: parsed.pm || {} };
    } catch (error) {
      return { am: {}, pm: {} };
    }
  }

  function writeDraft(draft) {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch (error) {
      /* best-effort — private browsing / storage disabled */
    }
  }

  function captureDraft(container) {
    const draft = { am: {}, pm: {} };
    container.querySelectorAll('[data-bundle-item]').forEach((card) => {
      const variantId = card.dataset.variantId;
      const amQty = getQty(card, 1);
      const pmQty = getQty(card, 2);
      if (amQty > 0) draft.am[variantId] = amQty;
      if (pmQty > 0) draft.pm[variantId] = pmQty;
    });
    return draft;
  }

  function applyDraft(container, draft) {
    container.querySelectorAll('[data-bundle-item]').forEach((card) => {
      const variantId = card.dataset.variantId;
      setQtyDisplay(card, 1, draft.am[variantId] || 0);
      setQtyDisplay(card, 2, draft.pm[variantId] || 0);
    });
  }

  // Sorted by variant id so the resulting string is deterministic
  // regardless of DOM/filter order — needed so a same-ingredients
  // comparison against a value restored from the cart (parsed once, not
  // rebuilt in DOM order) doesn't false-positive as "changed".
  function buildIngredientEntries(container, pack) {
    const entries = [];
    container.querySelectorAll('[data-bundle-item]').forEach((card) => {
      const qty = getQty(card, pack);
      if (qty <= 0) return;
      entries.push({
        variantId: card.dataset.variantId,
        title: card.dataset.productTitle || 'Item',
        qty,
      });
    });
    entries.sort((a, b) => (a.variantId > b.variantId ? 1 : a.variantId < b.variantId ? -1 : 0));
    return entries;
  }

  function formatIngredientList(entries) {
    return entries.map((entry) => `${entry.title} x${entry.qty}`).join(', ');
  }

  function formatIngredientData(entries) {
    return entries.map((entry) => `${entry.variantId}:${entry.qty}`).join('|');
  }

  function updateUI(container) {
    const state = getState(container);
    const amConfig = state.amConfig;
    const pmConfig = state.pmConfig;
    if (!amConfig || !pmConfig) return;

    const amQty = sumPack(container, 1);
    const pmQty = sumPack(container, 2);
    // Minimum is a combined AM+PM total (e.g. 4 total, split however the
    // member likes across the two packs) rather than AM alone having to
    // reach it before PM unlocks.
    const totalQty = amQty + pmQty;
    const totalMin = amConfig.min;
    const minMet = totalQty >= totalMin;

    const amLabel =
      !minMet
        ? `Pack 1: ${amQty} of ${amConfig.max} — need ${totalMin - totalQty} more`
        : `Pack 1: ${amQty} of ${amConfig.max}`;

    // Document-scoped, since the floating summary bar lives in <body>, not
    // inside this container.
    document.querySelectorAll('[data-bundle-count="1"]').forEach((el) => {
      el.textContent = amLabel;
    });
    document.querySelectorAll('[data-bundle-progress="1"]').forEach((el) => {
      el.style.width = `${Math.min(100, (amQty / amConfig.max) * 100)}%`;
    });
    document.querySelectorAll('[data-bundle-count="2"]').forEach((el) => {
      el.textContent = `Pack 2: ${pmQty} of ${pmConfig.max}`;
    });
    document.querySelectorAll('[data-bundle-progress="2"]').forEach((el) => {
      el.style.width = `${Math.min(100, (pmQty / pmConfig.max) * 100)}%`;
    });

    document.querySelectorAll('[data-bundle-plan-label]').forEach((el) => {
      el.textContent = pmQty > 0 ? '2 packs per day' : '1 pack per day';
    });

    // Same amQty/pmQty/min/max already computed above — these just render
    // them as separate short label + CTA text for the redesigned progress
    // card, instead of one combined sentence.
    document.querySelectorAll('[data-bundle-count-short="1"]').forEach((el) => {
      el.textContent = `${amQty} of ${amConfig.max} added`;
    });
    document.querySelectorAll('[data-bundle-cta="1"]').forEach((el) => {
      el.textContent = minMet ? 'Complete ✓' : `Add ${totalMin - totalQty} more to reach minimum`;
    });
    document.querySelectorAll('[data-bundle-count-short="2"]').forEach((el) => {
      el.textContent = `${pmQty} of ${pmConfig.max} added`;
    });
    document.querySelectorAll('[data-bundle-cta="2"]').forEach((el) => {
      el.textContent = pmQty >= pmConfig.max ? 'Max reached' : `Skip or add up to ${pmConfig.max}`;
    });
    document.querySelectorAll('[data-bundle-status-pill]').forEach((el) => {
      el.textContent = minMet ? 'Ready — review below' : `Add ${totalMin - totalQty} more to continue`;
      el.classList.toggle('byo-progress__status-pill--ready', minMet);
    });

    const amVariant = amConfig.variants[String(amQty)];
    const pmVariant = pmQty > 0 ? pmConfig.variants[String(pmQty)] : null;
    let totalCents = 0;
    if (amVariant) totalCents += amVariant.price;
    if (pmVariant) totalCents += pmVariant.price;
    document.querySelectorAll('[data-bundle-price]').forEach((el) => {
      el.textContent = formatMoney(totalCents);
    });

    container.querySelectorAll('[data-bundle-item]').forEach((card) => {
      const amBtnGroup = card.querySelector('[data-pack-stepper="1"]');
      if (amBtnGroup) {
        const inc = amBtnGroup.querySelector('[data-stepper-action="increase"]');
        const dec = amBtnGroup.querySelector('[data-stepper-action="decrease"]');
        if (inc) inc.disabled = amQty >= amConfig.max;
        if (dec) dec.disabled = getQty(card, 1) <= 0;
      }

      const pmBtnGroup = card.querySelector('[data-pack-stepper="2"]');
      if (pmBtnGroup) {
        const inc = pmBtnGroup.querySelector('[data-stepper-action="increase"]');
        const dec = pmBtnGroup.querySelector('[data-stepper-action="decrease"]');
        if (inc) inc.disabled = pmQty >= pmConfig.max;
        if (dec) dec.disabled = getQty(card, 2) <= 0;
      }
    });
  }

  // Adds/updates/removes the single priced AM or PM fee line, swapping to
  // whichever capsule-count variant matches the current total, and keeping
  // its hidden ingredient-list properties in sync with the current picks.
  async function syncFeeLine(container, pack) {
    const state = getState(container);
    const config = pack === 1 ? state.amConfig : state.pmConfig;
    const lineKeyProp = pack === 1 ? 'amFeeLineKey' : 'pmFeeLineKey';
    const variantIdProp = pack === 1 ? 'amFeeVariantId' : 'pmFeeVariantId';
    const dataProp = pack === 1 ? 'amFeeIngredientData' : 'pmFeeIngredientData';
    const qty = sumPack(container, pack);
    const amQty = sumPack(container, 1);
    const pmQty = sumPack(container, 2);
    // Combined AM+PM total must clear the overall minimum — either pack can
    // contribute toward it, so a pack only needs its own qty to be nonzero
    // (and within its own max) once that combined minimum is met.
    const totalMet = amQty + pmQty >= state.amConfig.min;

    const isValid = qty > 0 && qty <= config.max && totalMet;

    if (!isValid) {
      if (state[lineKeyProp]) {
        await fetchWithRetry(FoxTheme.routes.cart_change_url, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ id: state[lineKeyProp], quantity: 0 }),
        }).catch(() => {});
        state[lineKeyProp] = null;
        state[variantIdProp] = null;
        state[dataProp] = null;
        return true;
      }
      return false;
    }

    const variantInfo = config.variants[String(qty)];
    if (!variantInfo) return false;

    const entries = buildIngredientEntries(container, pack);
    const ingredientData = formatIngredientData(entries);

    // Already the correct line, at the correct tier, with the same
    // ingredients — nothing to do. Matters beyond efficiency: syncCart()
    // only notifies the cart drawer to refresh when something actually
    // changed (see below) specifically so a no-op sync — like every click
    // while still building below the minimum — never triggers that
    // refresh, which would otherwise echo back as another cart:updated
    // event and risk looping or racing with in-progress edits.
    if (state[lineKeyProp] && state[variantIdProp] === variantInfo.id && state[dataProp] === ingredientData) {
      return false;
    }

    if (state[lineKeyProp]) {
      // A tier or ingredient-composition change means a different line —
      // Shopify treats each variant+properties combination as its own
      // line, and line properties can't be edited in place via
      // cart/change, so swapping means removing the old line and adding
      // the new one rather than just updating quantity.
      await fetchWithRetry(FoxTheme.routes.cart_change_url, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body: JSON.stringify({ id: state[lineKeyProp], quantity: 0 }),
      }).catch(() => {});
      state[lineKeyProp] = null;
      state[variantIdProp] = null;
      state[dataProp] = null;
    }

    const response = await fetchWithRetry(FoxTheme.routes.cart_add_url, {
      ...FoxTheme.utils.fetchConfig('javascript'),
      body: JSON.stringify({
        items: [
          {
            id: variantInfo.id,
            quantity: 1,
            selling_plan: BUNDLE_SELLING_PLAN_ID,
            properties: {
              // Underscore-prefixed ones are hidden from the customer
              // everywhere (cart, checkout, order confirmation) but stay
              // visible to staff on the Admin order detail page. "Contains"
              // has no underscore, so Shopify's native cart/checkout/order
              // rendering shows it to the customer automatically — no
              // theme code needed on the checkout page itself for that.
              _pack_fee: PACK_LABELS[pack],
              _IngredientData: ingredientData,
              Contains: formatIngredientList(entries),
            },
          },
        ],
      }),
    });
    const result = await response.json();
    const addedItem = result.items && result.items[0];
    state[lineKeyProp] = addedItem ? addedItem.key : null;
    state[variantIdProp] = addedItem ? addedItem.variant_id : null;
    state[dataProp] = addedItem ? ingredientData : null;
    return true;
  }

  async function syncCart(container) {
    const state = getState(container);
    if (state.syncing) {
      state.pendingSync = true;
      return;
    }
    state.syncing = true;
    try {
      const amChanged = await syncFeeLine(container, 1).catch(() => false);
      const pmChanged = await syncFeeLine(container, 2).catch(() => false);

      // Clean up any old-style per-ingredient $0 lines left over from
      // before ingredients were consolidated onto the AM/PM Pack line.
      let legacyChanged = false;
      if (state.legacyLineKeys.length) {
        for (const key of state.legacyLineKeys) {
          await fetchWithRetry(FoxTheme.routes.cart_change_url, {
            ...FoxTheme.utils.fetchConfig('javascript'),
            body: JSON.stringify({ id: key, quantity: 0 }),
          }).catch(() => {});
        }
        state.legacyLineKeys = [];
        legacyChanged = true;
      }

      // Only notify (which triggers a cart-drawer refresh that echoes back
      // as a cart:updated event) when a real cart mutation happened — most
      // syncCart() calls while still building below the minimum are
      // genuine no-ops, and notifying anyway would cause resyncFromCart to
      // re-run on every click for no reason.
      if (amChanged || pmChanged || legacyChanged) {
        notifyCartChanged();
      }
    } catch (error) {
      /* best-effort */
    } finally {
      state.syncing = false;
      if (state.pendingSync) {
        state.pendingSync = false;
        syncCart(container);
      }
    }
  }

  // Rebuilds every stepper's on-screen quantity (and the tracked fee-line
  // state) strictly from an actual cart payload — a pack with a cart line
  // is restored from that line's recorded ingredient data, and a pack
  // without one is cleared to empty, so this always matches cart reality
  // (e.g. a cart clear or a manually-removed fee line correctly empties
  // the corresponding pack here too, rather than leaving stale steppers
  // behind). syncCart() only notifies the listener that calls this after
  // a genuine mutation (see syncCart), so it never runs mid-build purely
  // from a debounce echo. Caller is responsible for calling updateUI()
  // afterwards, since initContainer needs that to happen only after its
  // own button-disable/re-enable sequence.
  function resyncFromCart(container, cartJson) {
    const state = getState(container);
    if (!state.amConfig || !state.pmConfig) return;

    const amVariantIds = Object.values(state.amConfig.variants).map((v) => v.id);
    const pmVariantIds = Object.values(state.pmConfig.variants).map((v) => v.id);
    const cardsByVariantId = new Map();
    container.querySelectorAll('[data-bundle-item]').forEach((card) => {
      cardsByVariantId.set(String(card.dataset.variantId), card);
    });

    state.legacyLineKeys = [];

    let amCartData = null;
    let pmCartData = null;
    const legacyAm = {};
    const legacyPm = {};

    (cartJson.items || []).forEach((item) => {
      if (amVariantIds.includes(item.variant_id)) {
        amCartData = {
          key: item.key,
          variantId: item.variant_id,
          ingredientData: (item.properties && item.properties._IngredientData) || '',
        };
        return;
      }
      if (pmVariantIds.includes(item.variant_id)) {
        pmCartData = {
          key: item.key,
          variantId: item.variant_id,
          ingredientData: (item.properties && item.properties._IngredientData) || '',
        };
        return;
      }
      // Old-style per-ingredient $0 line from before ingredients were
      // consolidated onto the AM/PM Pack line itself — real, committed
      // cart data, so it's honored here, and gets queued for removal on
      // the next syncCart() now that it's redundant.
      const packLabel = item.properties && (item.properties._Pack || item.properties.Pack);
      if (!packLabel) return;
      if (!cardsByVariantId.has(String(item.variant_id))) return;
      if (packLabel === 'AM') legacyAm[item.variant_id] = item.quantity;
      else legacyPm[item.variant_id] = item.quantity;
      state.legacyLineKeys.push(item.key);
    });

    // Built fresh from cart truth every time — never from whatever was
    // previously in localStorage, so a pack with nothing backing it in the
    // cart always ends up empty here.
    const draft = { am: {}, pm: {} };

    if (Object.keys(legacyAm).length) draft.am = legacyAm;
    if (Object.keys(legacyPm).length) draft.pm = legacyPm;

    if (amCartData) {
      draft.am = {};
      amCartData.ingredientData.split('|').forEach((pair) => {
        if (!pair) return;
        const [variantId, qty] = pair.split(':');
        draft.am[variantId] = parseInt(qty, 10) || 0;
      });
    }
    if (pmCartData) {
      draft.pm = {};
      pmCartData.ingredientData.split('|').forEach((pair) => {
        if (!pair) return;
        const [variantId, qty] = pair.split(':');
        draft.pm[variantId] = parseInt(qty, 10) || 0;
      });
    }

    applyDraft(container, draft);
    writeDraft(draft);

    state.amFeeLineKey = amCartData ? amCartData.key : null;
    state.amFeeVariantId = amCartData ? amCartData.variantId : null;
    state.amFeeIngredientData = amCartData ? amCartData.ingredientData : null;
    state.pmFeeLineKey = pmCartData ? pmCartData.key : null;
    state.pmFeeVariantId = pmCartData ? pmCartData.variantId : null;
    state.pmFeeIngredientData = pmCartData ? pmCartData.ingredientData : null;
  }

  async function initContainer(container) {
    if (container.dataset.bundleInitialized) return;
    container.dataset.bundleInitialized = 'true';

    const state = getState(container);
    state.amConfig = parseConfig(container, 1);
    state.pmConfig = parseConfig(container, 2);
    if (!state.amConfig || !state.pmConfig) return;

    // Paint immediately from the local draft so there's no flash of empty
    // steppers while the cart fetch below is in flight.
    applyDraft(container, readDraft());
    updateUI(container);

    const allButtons = container.querySelectorAll('[data-stepper-action]');
    allButtons.forEach((button) => (button.disabled = true));

    try {
      const cartJson = await fetchCart();
      resyncFromCart(container, cartJson);
    } catch (error) {
      /* best-effort — if this fails, the builder still works from the local draft */
    }

    allButtons.forEach((button) => (button.disabled = false));
    // Re-run after the blanket re-enable above, so PM-locked and
    // at-maximum buttons end up correctly disabled again rather than left
    // enabled by that blanket pass.
    updateUI(container);

    // Any legacy lines found on load need cleaning up even though nothing
    // was clicked yet.
    if (state.legacyLineKeys.length) {
      syncCart(container);
    }

    initSummaryVisibility(container);
  }

  // The floating summary bar (.build-your-own__summary) has no visibility
  // logic of its own — it's simply part of the tab panel's normal content,
  // so it's only ever hidden by its panel being inactive. That's fine when
  // this tab starts inactive (the homepage, which defaults to a different
  // tab), but on a page where this is the only/active tab from load, the
  // bar would render fixed at the bottom of the viewport immediately,
  // overlapping whatever's there (e.g. a hero image) before the shopper
  // has scrolled anywhere near the builder. Shown only once the main
  // (non-fixed) progress card scrolls out of view, hidden while it's
  // visible — purely a display toggle, doesn't touch anything it displays.
  //
  // A plain scroll listener rather than IntersectionObserver: this section
  // sits far enough down some pages that the theme's own content-visibility:
  // auto optimization applies to it while off-screen, which was found to
  // stop the observer from firing again once the shopper actually scrolled
  // past it (confirmed via manual testing — the callback fired once on
  // attach and never again). Checking on scroll sidesteps that entirely.
  function initSummaryVisibility(container) {
    const progressCard = container.querySelector('.byo-progress');
    const summaryBar = container.querySelector('.build-your-own__summary');
    if (!progressCard || !summaryBar) return;

    const cartDrawer = document.getElementById('CartDrawer');

    let ticking = false;
    function update() {
      ticking = false;
      const scrolledPast = progressCard.getBoundingClientRect().bottom < 0;
      const cartOpen = !!cartDrawer && !cartDrawer.hasAttribute('hidden');
      summaryBar.classList.toggle('build-your-own__summary--visible', scrolledPast && !cartOpen);
    }
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    // The CSS body:has(cart-drawer#CartDrawer:not([hidden])) rule covers
    // this too, but :has() isn't supported in every mobile browser/webview
    // — this MutationObserver on the drawer's hidden attribute is the
    // reliable fallback that works everywhere.
    if (cartDrawer) {
      new MutationObserver(update).observe(cartDrawer, { attributes: true, attributeFilter: ['hidden'] });
    }

    update();
  }

  // True only when the tracked fee-line state doesn't match what the
  // current ingredient picks require — i.e. syncFeeLine would actually
  // need to add, swap, or remove a line. Checked before calling syncCart()
  // from the cart:updated listener below so an already-correct cart is a
  // no-op: syncCart's own notifyCartChanged() triggers the cart drawer to
  // refresh itself, which re-dispatches cart:updated — without this check
  // that echo would call syncCart again forever (visible as the checkout
  // button/warning flashing in a loop).
  function needsFeeCorrection(container) {
    const state = getState(container);
    if (!state.amConfig || !state.pmConfig) return false;
    if (state.legacyLineKeys.length) return true;

    const amQty = sumPack(container, 1);
    const pmQty = sumPack(container, 2);
    const totalMet = amQty + pmQty >= state.amConfig.min;
    const amValid = amQty > 0 && amQty <= state.amConfig.max && totalMet;
    const pmValid = pmQty > 0 && pmQty <= state.pmConfig.max && totalMet;

    const amExpectedVariant = amValid ? (state.amConfig.variants[String(amQty)] || {}).id || null : null;
    const pmExpectedVariant = pmValid ? (state.pmConfig.variants[String(pmQty)] || {}).id || null : null;

    if (amExpectedVariant !== state.amFeeVariantId || pmExpectedVariant !== state.pmFeeVariantId) return true;

    const amExpectedData = amValid ? formatIngredientData(buildIngredientEntries(container, 1)) : null;
    const pmExpectedData = pmValid ? formatIngredientData(buildIngredientEntries(container, 2)) : null;

    return amExpectedData !== (state.amFeeIngredientData || null) || pmExpectedData !== (state.pmFeeIngredientData || null);
  }

  // Cart lines can also change outside the builder entirely — removing an
  // ingredient or fee line from the cart drawer or cart page. Those flows
  // dispatch `cart:updated` with the fresh cart payload, so re-sync every
  // initialized builder on the page from it rather than leaving stale
  // steppers that would otherwise get silently re-added on the next click.
  document.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart) return;
    document.querySelectorAll('[data-bundle-builder]').forEach((container) => {
      if (container.dataset.bundleInitialized !== 'true') return;
      resyncFromCart(container, cart);
      updateUI(container);
      // If the change removed/altered the fee line while a valid build
      // still remains, this re-adds the correct line so a customer can't
      // check out with the fee line gone but ingredients still implied.
      if (needsFeeCorrection(container)) {
        syncCart(container);
      }
    });
  });

  function scanForContainers(root) {
    if (root.nodeType !== 1) return;
    if (root.matches('[data-bundle-builder]')) initContainer(root);
    root.querySelectorAll('[data-bundle-builder]').forEach(initContainer);
  }

  scanForContainers(document.documentElement);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => scanForContainers(node));
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('click', (event) => {
    const actionBtn = event.target.closest('[data-stepper-action]');
    if (!actionBtn || actionBtn.disabled) return;

    const card = actionBtn.closest('[data-bundle-item]');
    const stepperEl = actionBtn.closest('[data-pack-stepper]');
    const container = actionBtn.closest('[data-bundle-builder]');
    if (!card || !stepperEl || !container) return;

    const pack = parseInt(stepperEl.dataset.packStepper, 10);
    const state = getState(container);
    const config = pack === 1 ? state.amConfig : state.pmConfig;
    if (!config) return;

    const currentQty = getQty(card, pack);
    const action = actionBtn.dataset.stepperAction;

    if (action === 'increase') {
      if (sumPack(container, pack) >= config.max) return;
      setQtyDisplay(card, pack, currentQty + 1);
    } else if (action === 'decrease') {
      if (currentQty <= 0) return;
      setQtyDisplay(card, pack, currentQty - 1);
    } else {
      return;
    }

    updateUI(container);
    writeDraft(captureDraft(container));

    if (action === 'increase' && window.themeEcommerce) {
      window.themeEcommerce.pushEvent('add_to_cart', {
        currency: window.themeEcommerce.currency(),
        value: 0,
        items: [
          {
            item_id: card.dataset.variantId,
            item_name: card.dataset.productTitle,
            price: 0,
            quantity: 1,
            item_variant: PACK_LABELS[pack],
          },
        ],
      });

      if (window.themeEcommerce.pushHubSpotAddedToCart) {
        window.themeEcommerce.pushHubSpotAddedToCart({
          product_name: card.dataset.productTitle || null,
          sku: card.dataset.productSku || null,
          price: card.dataset.productPriceCents ? Number(card.dataset.productPriceCents) / 100 : null,
          quantity: 1,
          variant: PACK_LABELS[pack],
          image_url: card.dataset.productImage || null,
        });
      }
    }

    clearTimeout(container._bundleSyncTimeout);
    container._bundleSyncTimeout = setTimeout(() => syncCart(container), 500);
  });
}
